import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle, XCircle, Clock, Building2, Search, Eye, DollarSign, GraduationCap, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AccreditationAppsTab({ token }) {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [nominations, setNominations] = useState([]);
  const [allNominations, setAllNominations] = useState([]);
  const [loadingNominations, setLoadingNominations] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updating, setUpdating] = useState(null);
  const [updatingPayment, setUpdatingPayment] = useState(null);
  const [selectedTab, setSelectedTab] = useState('institutions');

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/accreditation-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchAllNominations = async () => {
    try {
      const res = await axios.get(`${API}/admin/all-accreditation-nominations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllNominations(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchStats(); fetchAllNominations(); }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const viewNominations = async (institutionId, institutionName) => {
    setLoadingNominations(true);
    setSelectedInstitution({ id: institutionId, name: institutionName });
    try {
      const res = await axios.get(`${API}/admin/institution-nominations/${institutionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNominations(res.data);
    } catch (err) { toast.error('Failed to load nominations'); }
    finally { setLoadingNominations(false); }
  };

  const updateStatus = async (appId, newStatus) => {
    setUpdating(appId);
    try {
      await axios.put(`${API}/admin/accreditation-applications/${appId}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Application ${newStatus}`);
      fetchStats();
    } catch (err) { toast.error('Failed to update status'); }
    finally { setUpdating(null); }
  };

  const updateAccreditationPaymentStatus = async (appId, status) => {
    setUpdatingPayment(appId);
    try {
      await axios.put(`${API}/admin/accreditation-applications/${appId}/payment-status`, {
        status: status
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Payment marked as ${status}`);
      fetchStats();
    } catch (err) { toast.error('Failed to update payment status'); }
    finally { setUpdatingPayment(null); }
  };

  const updateNominationPaymentStatus = async (nominationId, status) => {
    setUpdatingPayment(nominationId);
    try {
      await axios.put(`${API}/admin/nominations/${nominationId}/payment-status`, {
        status: status
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Payment marked as ${status}`);
      // Refresh nominations
      if (selectedInstitution) {
        viewNominations(selectedInstitution.id, selectedInstitution.name);
      }
      fetchStats();
    } catch (err) { toast.error('Failed to update payment status'); }
    finally { setUpdatingPayment(null); }
  };

  const statusColor = (s) => {
    const m = { 
      submitted: 'bg-blue-100 text-blue-700', 
      approved: 'bg-green-100 text-green-700', 
      rejected: 'bg-red-100 text-red-700',
      under_review: 'bg-amber-100 text-amber-700'
    };
    return m[s] || 'bg-slate-100 text-slate-700';
  };

  const feeStatusColor = (s) => {
    if (s === 'completed') return 'bg-green-100 text-green-700';
    if (s === 'pending_verification') return 'bg-blue-100 text-blue-700';
    return 'bg-amber-100 text-amber-700';
  };

  const paymentStatusText = (s) => {
    if (s === 'completed') return 'Paid';
    if (s === 'pending_verification') return 'Pending Verification';
    return 'Pending';
  };

  const filteredStats = statusFilter === 'all' ? stats : stats.filter(s => s.status === statusFilter);

  // Summary stats
  const totalInstitutions = stats.length;
  const approvedInstitutions = stats.filter(s => s.status === 'approved').length;
  const totalNominations = stats.reduce((sum, s) => sum + (s.total_nominations || 0), 0);
  const paidNominations = stats.reduce((sum, s) => sum + (s.paid_nominations || 0), 0);
  const totalExamFees = stats.reduce((sum, s) => sum + (s.total_exam_fees_collected || 0), 0);
  const totalAccredFees = stats.filter(s => s.accreditation_fee_status === 'completed').reduce((sum, s) => sum + (s.accreditation_fee_amount || 0), 0);

  if (loading) return <p className="text-sm text-slate-500 py-8 text-center">Loading accreditation data...</p>;

  return (
    <div data-testid="admin-accreditation-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <Card className="bg-blue-50">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-blue-700">{totalInstitutions}</p>
            <p className="text-xs text-blue-600">Total Institutions</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-green-700">{approvedInstitutions}</p>
            <p className="text-xs text-green-600">Approved</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-purple-700">{totalNominations}</p>
            <p className="text-xs text-purple-600">Total Nominations</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-emerald-700">{paidNominations}</p>
            <p className="text-xs text-emerald-600">Paid Nominations</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="p-3 text-center">
            <p className="text-lg font-bold text-amber-700">₹{(totalAccredFees/1000).toFixed(0)}K</p>
            <p className="text-xs text-amber-600">Accred. Fees</p>
          </CardContent>
        </Card>
        <Card className="bg-[#C5A572]/20">
          <CardContent className="p-3 text-center">
            <p className="text-lg font-bold text-[#C5A572]">₹{(totalExamFees/1000).toFixed(0)}K</p>
            <p className="text-xs text-[#9A8463]">Exam Fees</p>
          </CardContent>
        </Card>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-2 mb-4">
        <Button onClick={() => setSelectedTab('institutions')} className={`rounded-sm ${selectedTab === 'institutions' ? 'bg-[#1B2A4A] text-white' : 'bg-slate-100 text-slate-700'}`}>
          Institutions ({stats.length})
        </Button>
        <Button onClick={() => setSelectedTab('nominations')} className={`rounded-sm ${selectedTab === 'nominations' ? 'bg-[#1B2A4A] text-white' : 'bg-slate-100 text-slate-700'}`}>
          Student Nominations ({allNominations.length})
        </Button>
      </div>

      {selectedTab === 'institutions' ? (
        <>
          {/* Filter */}
          <div className="flex items-center gap-4 mb-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 rounded-sm">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Applications</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="under_review">Under Review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500">{filteredStats.length} institutions</span>
      </div>

      {/* Institution List with Stats */}
      <div className="space-y-3">
        {filteredStats.map(inst => (
          <Card key={inst.id} className="hover:border-[#C5A572] transition-colors">
            <CardContent className="p-4">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Institution Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-[#1B2A4A]">{inst.institution_name}</h4>
                    <Badge className={`text-xs ${statusColor(inst.status)}`}>{inst.status}</Badge>
                  </div>
                  <p className="text-sm text-slate-500">{inst.city}, {inst.country}</p>
                  <p className="text-xs text-slate-400 mt-1">{inst.applicant_email}</p>
                </div>

                {/* Nomination Stats */}
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-lg font-bold text-purple-600">{inst.total_nominations}</p>
                    <p className="text-xs text-slate-500">Nominations</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-600">{inst.paid_nominations}</p>
                    <p className="text-xs text-slate-500">Paid</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-amber-600">{inst.unpaid_nominations}</p>
                    <p className="text-xs text-slate-500">Unpaid</p>
                  </div>
                </div>

                {/* Fee Status */}
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Accred. Fee</p>
                    <Badge className={`text-xs ${feeStatusColor(inst.accreditation_fee_status)}`}>
                      {paymentStatusText(inst.accreditation_fee_status)}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Exam Fees</p>
                    <p className="text-sm font-medium text-[#C5A572]">₹{(inst.total_exam_fees_collected || 0).toLocaleString()}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-2">
                  {inst.total_nominations > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm text-xs"
                      onClick={() => viewNominations(inst.user_id, inst.institution_name)}
                    >
                      <Users className="h-3 w-3 mr-1" /> View Students
                    </Button>
                  )}
                  
                  {/* Payment Status Buttons */}
                  {inst.accreditation_fee_status !== 'completed' && (
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white rounded-sm text-xs"
                      onClick={() => updateAccreditationPaymentStatus(inst.id, 'received')}
                      disabled={updatingPayment === inst.id}
                    >
                      <DollarSign className="h-3 w-3 mr-1" /> Mark Paid
                    </Button>
                  )}
                  {inst.accreditation_fee_status === 'completed' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm text-xs border-amber-500 text-amber-600 hover:bg-amber-50"
                      onClick={() => updateAccreditationPaymentStatus(inst.id, 'pending')}
                      disabled={updatingPayment === inst.id}
                    >
                      <Clock className="h-3 w-3 mr-1" /> Mark Pending
                    </Button>
                  )}
                  
                  {inst.status === 'submitted' && (
                    <>
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm text-xs"
                        onClick={() => updateStatus(inst.id, 'approved')}
                        disabled={updating === inst.id}
                      >
                        <CheckCircle className="h-3 w-3 mr-1" /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="rounded-sm text-xs"
                        onClick={() => updateStatus(inst.id, 'rejected')}
                        disabled={updating === inst.id}
                      >
                        <XCircle className="h-3 w-3 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Nominations Modal */}
      {selectedInstitution && (
        <Dialog open={!!selectedInstitution} onOpenChange={() => setSelectedInstitution(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Student Nominations - {selectedInstitution.name}</DialogTitle>
            </DialogHeader>
            {loadingNominations ? (
              <p className="text-center py-8 text-slate-500">Loading...</p>
            ) : nominations.length === 0 ? (
              <p className="text-center py-8 text-slate-500">No nominations found</p>
            ) : (
              <div className="space-y-3">
                {nominations.map(nom => {
                  const isPendingVerification = nom.payment_status === 'pending_verification';
                  const isPaid = nom.payment_status === 'paid';
                  return (
                    <div key={nom.id} className={`p-4 border rounded-sm ${isPendingVerification ? 'border-blue-300 bg-blue-50/50' : ''}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium">{nom.full_name}</p>
                          <p className="text-xs text-slate-500">{nom.email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{nom.examination}</Badge>
                            <Badge className={`text-xs ${statusColor(nom.status)}`}>{nom.status}</Badge>
                          </div>
                          {nom.bank_transfer && (
                            <div className="mt-2 p-2 bg-slate-100 rounded-sm text-xs">
                              <p className="font-medium text-slate-700">Bank Transfer Details:</p>
                              <p>Ref: {nom.bank_transfer.reference} | Bank: {nom.bank_transfer.bank_name}</p>
                              <p>Date: {nom.bank_transfer.date} | Amount: ₹{nom.bank_transfer.amount?.toLocaleString()}</p>
                            </div>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <Badge className={`text-xs ${isPaid ? 'bg-green-100 text-green-700' : isPendingVerification ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                            {isPaid ? 'Fee Paid' : isPendingVerification ? 'Pending Verification' : 'Fee Pending'}
                          </Badge>
                          {nom.allocated_center && (
                            <p className="text-xs text-slate-500 mt-1">{nom.allocated_center}</p>
                          )}
                          
                          {/* Payment Status Actions */}
                          <div className="flex gap-1 mt-2 justify-end">
                            {!isPaid && (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white rounded-sm text-xs h-7"
                                onClick={() => updateNominationPaymentStatus(nom.id, 'received')}
                                disabled={updatingPayment === nom.id}
                              >
                                <DollarSign className="h-3 w-3 mr-1" /> Mark Paid
                              </Button>
                            )}
                            {isPaid && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-sm text-xs h-7 border-amber-500 text-amber-600"
                                onClick={() => updateNominationPaymentStatus(nom.id, 'pending')}
                                disabled={updatingPayment === nom.id}
                              >
                                <Clock className="h-3 w-3 mr-1" /> Mark Pending
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
        </>
      ) : (
        /* Student Nominations Tab */
        <div>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <Card className="bg-blue-50"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-blue-700">{allNominations.length}</p><p className="text-xs text-blue-600">Total</p></CardContent></Card>
            <Card className="bg-amber-50"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-amber-700">{allNominations.filter(n => n.payment_status === 'pending' || !n.payment_status).length}</p><p className="text-xs text-amber-600">Pending Payment</p></CardContent></Card>
            <Card className="bg-blue-50"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-blue-700">{allNominations.filter(n => n.payment_status === 'pending_verification').length}</p><p className="text-xs text-blue-600">Pending Verification</p></CardContent></Card>
            <Card className="bg-green-50"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-green-700">{allNominations.filter(n => n.payment_status === 'paid' || n.payment_status === 'received' || n.payment_status === 'completed').length}</p><p className="text-xs text-green-600">Paid</p></CardContent></Card>
          </div>

          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8FAFC]">
                <TableHead className="text-xs font-bold">Student</TableHead>
                <TableHead className="text-xs font-bold">Institution</TableHead>
                <TableHead className="text-xs font-bold">Examination</TableHead>
                <TableHead className="text-xs font-bold">Fee</TableHead>
                <TableHead className="text-xs font-bold">Payment</TableHead>
                <TableHead className="text-xs font-bold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allNominations.map((nom) => {
                const isPaid = nom.payment_status === 'paid' || nom.payment_status === 'received' || nom.payment_status === 'completed';
                const isPendingVerification = nom.payment_status === 'pending_verification';
                return (
                  <TableRow key={nom.id}>
                    <TableCell>
                      <p className="text-sm font-medium">{nom.full_name}</p>
                      <p className="text-xs text-slate-500">{nom.email}</p>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{nom.institution_name || nom.accreditation_id?.slice(0,8) || '-'}</TableCell>
                    <TableCell><Badge className="bg-slate-100 text-slate-700">{nom.examination}</Badge></TableCell>
                    <TableCell className="font-medium">₹{(nom.discounted_fee || nom.fee || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${isPaid ? 'bg-green-100 text-green-700' : isPendingVerification ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                        {isPaid ? 'Paid' : isPendingVerification ? 'Pending Verification' : 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {isPendingVerification && (
                        <div className="flex gap-1">
                          <Button size="sm" className="h-6 px-2 text-xs bg-green-600 text-white rounded-sm" onClick={() => updateNominationPaymentStatus(nom.id, 'received')}>
                            <CheckCircle className="h-3 w-3 mr-1" />Verify & Approve
                          </Button>
                          <Button size="sm" variant="outline" className="h-6 px-2 text-xs rounded-sm text-red-600" onClick={() => updateNominationPaymentStatus(nom.id, 'pending')}>
                            Reject
                          </Button>
                        </div>
                      )}
                      {!isPaid && !isPendingVerification && (
                        <Button size="sm" className="h-6 px-2 text-xs bg-green-600 text-white rounded-sm" onClick={() => updateNominationPaymentStatus(nom.id, 'received')}>
                          Mark Paid
                        </Button>
                      )}
                      {isPaid && (
                        <span className="text-xs text-green-600">✓ Complete</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default AccreditationAppsTab;
